export type PaymentInformation = {
  firstname?: string | null;
  lastname?: string | null;
  email: string;
  birthDate?: string | null;
  civility?: string | null;
  businessName?: string | null;
  identificationNumber?: string | null;
  referenceId?: string;
  referenceDocumentId?: string;
  referenceDocumentUrl?: string;
  customerId: string;
  franchiseId: string;
  agencyId: string;
  agencyName: string;
  totalAmount: number;
};

export type Extend = {
  id: string;
  documentId: string;
  franchiseId: string;
  pickupDate: string;
  dropoffDate: string;
  extendTotalDuration: number;
  totalKilometers: number;
  totalAdditionalKilometers: number;
  discountPercent: number;
  updateRequestDate: string;
  updateRequestExpirationDate: string;
  origin: string;
  priceTypeCode: string;
  category: {
    id: string;
    vehicleExample: string;
    vehicleLabel: string;
    vehicleImages: string[];
  };
  pickupAgency: {
    id: string;
    name: string;
    companyRegionCode: string;
    code: string;
  };
  dropoffAgency: {
    id: string;
    name: string;
    companyRegionCode: string;
    code: string;
  };
  price: {
    additionalDayPrice: number;
    additionalKilometersPrice: number;
    discountPrice: number;
    forfaitPrice: number;
    updatePrice: number;
    totalPrice: number;
  };
};

export type ExtendPaymentInformation = PaymentInformation & {
  information: Extend;
};

export type GenericLinkPaymentInformation = PaymentInformation & {
  creationDatetime?: string | null;
  referenceDatetime?: string | null;
  pickupDatetime?: string | null;
  dropoffDatetime?: string | null;
  plateNumber?: string | null;
  bcc?: string | null;
  agencyPhone?: string | null;
  information: {
    endDate: string;
    totalBalance: number;
    amount: number;
    nbDaysSupp: number;
    nbKmSupp: number;
    vehicleCategoryCode: string;
    vehicleCategoryLabel: string;
    devise: string;
    referenceCustomerId: string;
    customerData: {
      civility?: string | null;
      lastName?: string | null;
      firstName?: string | null;
      businessName?: string | null;
      siret?: string | null;
      birthdate?: string | null;
    };
    startAgencyExternalReferenceId: string;
    mail: string;
    identifier: string;
    agenceCode: string;
    regionCode: string;
  };
};

export type OptionsPaymentInformation = PaymentInformation & {
  information: {
    companyRegionCode: string;
    bookingId: string;
    options: {
      id: string;
      quantity: number;
      duration: number;
      unitPrice: number;
      title: string;
      description: string;
      totalPrice: number;
      isPricePerDay: boolean;
      isInsurance: boolean;
    }[];
  };
};

export type LoyaltyCardPaymentInformation = PaymentInformation & {
  information: {
    customerId: string | null;
    businessName: string;
    siret: string;
    rawAddress: string;
    zipCode: string;
    city: string;
    country: string;
    phoneNumber: string;
    schemaId: string;
    agencyId: string;
    mail: string;
    firstname: string;
    lastname: string;
    companyType: string;
    codeNaf: string;
    purchaseDate: string;
    endSubscriptionDate: string;
    subscriptionDate: string;
  };
};

export type WebBookingOption = {
  title: string;
  description: string;
  isPricePerDay: boolean;
  isInsurance: boolean;
  id: string;
  quantity: number;
  duration: number;
  unitPrice: number;
  totalPrice: number;
};

export type WebBooking = {
  pickupCompanyRegionCode: string;
  pickupAgencyCode: string;
  pickupAgencyName?: string;
  dropoffCompanyRegionCode: string;
  dropoffAgencyCode: string;
  dropoffAgencyName?: string;
  pickupDate: string;
  dropoffDate: string;
  kilometers: number;
  duration: number;
  categoryType: string;
  isFlex: boolean;
  sendEmail: boolean;
  category: {
    motorizationTypeLabel: string;
    gearboxTypeLabel: string;
    ageRequired: number;
    isWithoutLicense: boolean;
    drivingLicenseYearMinimum: number;
    price: {
      price: number;
      flexPrice?: number;
      flexRate?: number;
      currency: string;
      priceType: string;
      additionalKilometersPrice: number;
      additionalDayPrice: number;
    };
    termsOfUse: {
      additionalDayPrice: number;
      additionalKmPrice: number;
    };
    id: string;
    vehicleExample: string;
    vehicleLabel: string;
    vehicleImages: string[];
  };
  insurance: {
    franchiseAmount: number;
    title: string;
    description: string;
    isPricePerDay: boolean;
    isInsurance: boolean;
    id: string;
    quantity: number;
    duration: number;
    unitPrice: number;
    totalPrice: number;
  };
  options: WebBookingOption[];
  customer: {
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    company?: string | null;
    phone: {
      countryCode: string;
      phoneNumber: string;
    };
  };
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
};

export type LinkBooking = {
  bookingId: string;
  startDate: string;
  endDate: string;
  kilometers: number;
  category: {
    id: string;
    vehicleExample?: string;
    vehicleLabel: string;
    price: {
      price: number;
      currency: string;
    };
  };
  startAgency: {
    name: string;
    regionCode: string;
  };
  endAgency: {
    name: string;
    regionCode: string;
  };
  options: WebBookingOption[];
  insurance?: {
    franchiseAmount: number;
    title: string;
    description: string;
    isPricePerDay: boolean;
    isInsurance: boolean;
    id: string;
    quantity: number;
    duration: number;
    unitPrice: number;
    totalPrice: number;
  } | null;
};

export type GenericLinkBooking = {
  referenceBookingId: string;
  customerData?: {
    civility?: string | null;
    lastName?: string | null;
    firstName?: string | null;
    businessName?: string | null;
    siret?: string | null;
    birthdate?: string | null;
  } | null;
  startAgencyData: {
    label: string;
    agenceCode: string;
    regionCode: string;
  };
  endAgencyData: {
    label: string;
    agenceCode: string;
    regionCode: string;
  };
  startDate: string;
  endDate: string;
  scope: string;
  vehicleCategoryCode: string;
  vehicleCategoryLabel: string;
  items: unknown[];
  forecastKilometers: number;
  totalAmount: number;
  devise: string;
};

export type BookingCreationInformation = PaymentInformation & {
  information: WebBooking;
};

export type BookingLinkCreationInformation = PaymentInformation & {
  orderType?: string;
  information: LinkBooking;
  cacheId: string;
};

export type GenericLinkBookingCreationInformation = PaymentInformation & {
  orderType?: string;
  information: GenericLinkBooking;
};

export type BookingLinkCreationIndividualCustomer = {
  civility: string;
  firstName: string;
  lastName: string;
  birthDate: string;
};

export type BookingLinkCreationCustomerUpdate = {
  individualCustomer: BookingLinkCreationIndividualCustomer;
  businessCustomer: {
    identificationNumber: string;
    businessName: string;
  };
};

export enum PaymentType {
  extend = "extend",
  fine = "fine",
  invoice = "invoice",
  offense = "offense",
  options = "options",
  loyaltyCard = "loyaltyCard",
  bookingCreation = "bookingCreation",
  bookingLinkCreation = "bookingLinkCreation",
  ExtendPaymentLink = "extendPaymentLink",
  createBookingPaymentLink = "createBookingPaymentLink",
  toll = "Toll",
}

export type PaymentHistory = {
  id: string;
  paymentType: string;
  creationDate: string;
  status: string;
  paymentDate: string;
  externalReference: string;
  token: string;
  logMessage: string;
};
